import { Box, Grid, Typography, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { 
  LineChart, 
  PieChart, 
  Users, 
} from "lucide-react";
import LogoBig from './LogoBig';

// ================================|| ANIMATIONS ||================================ //

const float1 = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0, 0) scale(1); }
`;

const float2 = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-30px, 50px) scale(1.15); }
  66% { transform: translate(20px, -20px) scale(0.85); }
  100% { transform: translate(0, 0) scale(1); }
`;

const barAnimate = keyframes`
  0% { height: 10%; opacity: 0.2; }
  50% { height: 80%; opacity: 0.8; }
  100% { height: 10%; opacity: 0.2; }
`;

// ================================|| STYLED COMPONENTS ||================================ //

const FeatureIcon = styled(Box)(({ theme }) => {
  const color = theme.palette.authSecondary?.main || '#00E676';
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '10px',
    backgroundColor: `${color}1A`, // ~10% opacity
    color: color,
    border: `1px solid ${color}33`, // ~20% opacity
    marginRight: '16px'
  };
});

const AuthSidePanel = () => {
    const backgroundBars = Array.from({ length: 24 });

    const features = [
        {
            title: "Integrated Charting UI",
            desc: "Professional interface with indicators, pattern analysis, and limit order tracing.",
            icon: <LineChart size={20} />
        },
        {
            title: "TradeLog Performance Analytics",
            desc: "Auto-log positions and calculate key portfolio KPIs with visual graph results.",
            icon: <PieChart size={20} />
        },
        {
            title: "Social Trading Hub",
            desc: "Share ideas, manage friends, and climb the performance-based leaderboard.",
            icon: <Users size={20} />
        },
    ];

    return (
        <Grid
            item
            xs={12}
            md={6}
            sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                background: (theme) => `linear-gradient(135deg, ${theme.palette.dark?.dark || '#07090F'} 0%, ${theme.palette.dark?.main || '#111827'} 100%)`,
                minHeight: '100vh',
                p: 1,
            }}
        >
            {/* Animated Background Elements */}
            <Box sx={{
                position: "absolute", top: "-10%", left: "-10%", width: "60%", height: "60%",
                background: "radial-gradient(circle, rgba(0,229,255,0.12) 0%, rgba(0,229,255,0) 70%)",
                filter: "blur(80px)",
                animation: `${float1} 15s infinite ease-in-out`,
            }} />

            <Box sx={{
                position: "absolute", bottom: "-10%", right: "-10%", width: "60%", height: "60%",
                background: "radial-gradient(circle, rgba(0,230,118,0.12) 0%, rgba(0,230,118,0) 70%)",
                filter: "blur(80px)",
                animation: `${float2} 18s infinite ease-in-out reverse`,
            }} />

            {/* Animated Market Chart Background */}
            <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '25%',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                px: 2, opacity: 0.3, gap: 0.5, zIndex: 0
            }}>
                {backgroundBars.map((_, i) => (
                    <Box key={i} sx={{
                        width: '100%',
                        background: `linear-gradient(to top, rgba(29, 233, 182, 0.05), rgba(0, 230, 118, 0.4))`,
                        borderRadius: '2px 2px 0 0',
                        animation: `${barAnimate} ${3 + (i % 5)}s infinite ease-in-out`,
                        animationDelay: `${i * 0.15}s`,
                    }} />
                ))}
            </Box>

            {/* Main Content Container */}
            <Box sx={{
                position: "relative",
                zIndex: 1,
                backdropFilter: "blur(16px)",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: 6,
                p: { md: 5, lg: 7 },
                width: '100%',
                maxWidth: 580,
                boxShadow: "0 40px 80px rgba(0, 0, 0, 0.4)",
            }}>
                {/* Header Section */}
                <Box sx={{ mb: 5 }}>
                    <LogoBig />
                    <Typography variant="h5" sx={{ color: "rgba(255,255,255,1)", mt:3, lineHeight: 1.5 }}>
                        Our stock trading simulation platform lets you practice real-time trading, test complex strategies, and engage with a community of insights.
                    </Typography>
                </Box>

                {/* Features List */}
                <List disablePadding>
                    {features.map((feature, index) => (
                        <ListItem key={index} disableGutters sx={{ mb: 0, alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 'auto' }}>
                                <FeatureIcon>
                                    {feature.icon}
                                </FeatureIcon>
                            </ListItemIcon>
                            <ListItemText 
                                primary={
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff', mb: 0 }}>
                                        {feature.title}
                                    </Typography>
                                }
                                secondary={
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                                        {feature.desc}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Grid>
    );
};

export default AuthSidePanel;